import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { usePDF } from "@react-pdf/renderer";
import download from "downloadjs";

import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";

import BoutonRond from "../Design/Boutons/BoutonRond";
import PrintIcon from "@material-ui/icons/Print";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";

import MonStyle from "./DocumentPdf.module.css";
import { hauteurNavigateur } from "../functions/Navigateur";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Worker PDF.js (fix "fake worker failed")
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export default function DocumentPdf({
  openDocumentPdf,
  onClose,
  autoPrint = false,
  nomFichier = "document.pdf",
  children,
}) {
  // IMPORTANT : on récupère updateInstance pour forcer la régénération
  const [instance, updateInstance] = usePDF({ document: children });

  const iframePrintRef = useRef(null);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  // Quand on ouvre / quand le contenu change => régénérer le PDF
  useEffect(() => {
    if (openDocumentPdf) {
      updateInstance(children);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDocumentPdf, children]);

  // Reset à la fermeture
  useEffect(() => {
    if (!openDocumentPdf) {
      setNumPages(null);
      setPageNumber(1);
    }
  }, [openDocumentPdf]);

  const scale = useMemo(() => {
    const hauteurDefaut = 840;
    const hauteurTotal = hauteurNavigateur();
    const hauteurAttendue = hauteurTotal * 0.8;
    return hauteurAttendue / hauteurDefaut;
  }, [openDocumentPdf]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDownload = () => {
    if (instance?.blob) download(instance.blob, nomFichier);
  };

  // Impression propre SANS nouvel onglet : iframe cachée
  const printPdfOnly = useCallback(() => {
    if (!instance?.url) return;

    const iframe = iframePrintRef.current;
    if (!iframe) return;

    // On recharge le PDF dans l'iframe, puis on imprime quand c'est chargé
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error("Erreur impression PDF :", e);
        }
      }, 1200);
    };

    iframe.src = instance.url;
  }, [instance?.url]);

  const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

  const onPrint = () => {
    if (isFirefox) {
      printPdfOnly();
      return;
    }

    if (!instance?.blob) return;

    const pdfBlob = new Blob([instance.blob], {
      type: "application/pdf",
    });

    const pdfUrl = URL.createObjectURL(pdfBlob);

    const printWindow = window.open(pdfUrl, "_blank");
    console.log(instance.blob?.type);
    if (!printWindow) {
      console.error("Impossible d'ouvrir le PDF pour impression");
      URL.revokeObjectURL(pdfUrl);
      return;
    }

    // On libère l'URL plus tard, une fois que le navigateur a eu le temps de la charger
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 60000);
  };

  // autoPrint (si tu l’utilises) => on attend que l’URL soit prête
  useEffect(() => {
    if (openDocumentPdf && autoPrint && instance?.url) {
      const t = setTimeout(() => printPdfOnly(), 300);
      return () => clearTimeout(t);
    }
  }, [openDocumentPdf, autoPrint, instance?.url, printPdfOnly]);

  if (instance.loading) return <div>Loading PDF…</div>;
  if (instance.error) return <div>Erreur PDF : {String(instance.error)}</div>;

  return (
    <Modal
      open={openDocumentPdf}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Fade in={openDocumentPdf}>
        <div className={MonStyle.Page}>
          {/* Barre outils: on force un z-index pour ne pas perdre les clics */}
          <div className={MonStyle.BarreOutils} style={{ position: "relative", zIndex: 10 }}>
            <div className={MonStyle.BoutonFermer}>
              <BoutonRond
                couleur="vert"
                iconeComposant={<CloseIcon />}
                couleurIcone="blanc"
                onClick={onClose}
                tooltip="Fermer"
              />
            </div>

            <div className={MonStyle.BoutonsActions}>
              <BoutonRond
                couleur="vert"
                iconeComposant={<GetAppIcon />}
                couleurIcone="blanc"
                onClick={onDownload}
                style={{ marginRight: "10px" }}
                tooltip="Télécharger"
              />
              <BoutonRond
                couleur="vert"
                iconeComposant={<PrintIcon />}
                couleurIcone="blanc"
                onClick={onPrint}
                tooltip="Imprimer"
              />
            </div>
          </div>

          {/* Viewer PDF */}
          <div style={{ position: "relative", zIndex: 1 }}>
            <Document
              file={instance.blob}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div>Chargement…</div>}
              error={<div>Impossible d’afficher le PDF.</div>}
            >
              <Page pageNumber={pageNumber} scale={scale} />
            </Document>
          </div>

          {numPages && (
            <div className={MonStyle.NumeroPage} style={{ position: "relative", zIndex: 10 }}>
              <div className={MonStyle.BoutonsPage}>
                <BoutonRond
                  iconeComposant={<ArrowBackIosIcon />}
                  couleurIcone="blanc"
                  disabled={pageNumber === 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  tooltip="Page précédente"
                />
                <div className={MonStyle.TexteNumeroPage}>
                  {`Page ${pageNumber} / ${numPages}`}
                </div>
                <BoutonRond
                  iconeComposant={<ArrowForwardIosIcon />}
                  couleurIcone="blanc"
                  disabled={pageNumber === numPages}
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  tooltip="Page suivante"
                />
              </div>
            </div>
          )}

          {/* Iframe cachée dédiée à l’impression (pas de nouvel onglet) */}
          <iframe
            ref={iframePrintRef}
            title="print-pdf"
            style={{
              position: "fixed",
              width: "1px",
              height: "1px",
              right: "0",
              bottom: "0",
              opacity: 0,
              border: 0,
              pointerEvents: "none",
            }}
          />
        </div>
      </Fade>
    </Modal>
  );
}
